import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  RefreshCcw,
  Loader2,
  Building2,
  FileText,
  ExternalLink,
  ArrowUpRight,
  Maximize2,
  X,
} from "lucide-react";

const formatNumber = (val) => Number(val || 0).toLocaleString("en-IN");

export default function KycRequests() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalCount: 0,
  });

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Review Modal State
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [processingStatus, setProcessingStatus] = useState(false);
  const [copiedField, setCopiedField] = useState("");

  // Fullscreen Image Lightbox State
  const [previewImage, setPreviewImage] = useState(null);

  const fetchKycList = useCallback(
    async (nextPage = page) => {
      try {
        setLoading(true);
        setError("");
        const params = {
          page: nextPage,
          limit: 20,
        };

        if (statusFilter !== "all") params.status = statusFilter;
        if (searchQuery.trim()) params.search = searchQuery.trim();

        const { data } = await API.get("/admin/kyc", { params });
        if (!data?.success) throw new Error(data?.message || "Failed to load KYC requests");

        setRequests(Array.isArray(data.data) ? data.data : []);
        if (data.stats) setStats(data.stats);
        setPage(Number(data.page || nextPage));
        setTotalPages(Number(data.totalPages || 1));
        setTotal(Number(data.total || 0));
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load KYC requests");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, searchQuery, page]
  );

  useEffect(() => {
    fetchKycList(1);
  }, [statusFilter, searchQuery, fetchKycList]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requests.map((r) => r._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "kyc",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccessMessage(`Successfully deleted ${data.deletedCount || selectedIds.length} KYC record(s)`);
        fetchKycList(page);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const openReviewModal = (item) => {
    setSelectedKyc(item);
    setRejectionReason(item.rejectionReason || "");
    setAdminNote(item.adminNote || "");
    setIsModalOpen(true);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedKyc) return;
    try {
      setProcessingStatus(true);
      setError("");
      setSuccessMessage("");

      if (newStatus === "rejected" && !rejectionReason.trim()) {
        setError("Please enter a reason for rejecting the KYC submission.");
        setProcessingStatus(false);
        return;
      }

      const { data } = await API.patch(`/admin/kyc/${selectedKyc._id}/status`, {
        status: newStatus,
        rejectionReason: rejectionReason.trim(),
        adminNote: adminNote.trim(),
      });

      if (!data?.success) throw new Error(data?.message || "Failed to update status");

      setSuccessMessage(
        `KYC for ${selectedKyc.user?.name || "User"} ${newStatus} successfully! ${
          data?.notification?.sent ? "Push notification sent to user." : ""
        }`
      );

      setIsModalOpen(false);
      fetchKycList(page);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Error updating KYC status");
    } finally {
      setProcessingStatus(false);
    }
  };

  const statCards = useMemo(
    () => [
      {
        label: "Pending Reviews",
        value: stats.pendingCount,
        subtext: "Waiting for admin verification",
        icon: Clock,
        color: "text-amber-500 bg-amber-500/10 border-amber-500/20",
        active: statusFilter === "pending",
        onClick: () => {
          setStatusFilter("pending");
          setPage(1);
        },
      },
      {
        label: "Approved KYCs",
        value: stats.approvedCount,
        subtext: "Verified & eligible for payouts",
        icon: CheckCircle2,
        color: "text-green-500 bg-green-500/10 border-green-500/20",
        active: statusFilter === "approved",
        onClick: () => {
          setStatusFilter("approved");
          setPage(1);
        },
      },
      {
        label: "Rejected KYCs",
        value: stats.rejectedCount,
        subtext: "Documents declined",
        icon: XCircle,
        color: "text-red-500 bg-red-500/10 border-red-500/20",
        active: statusFilter === "rejected",
        onClick: () => {
          setStatusFilter("rejected");
          setPage(1);
        },
      },
      {
        label: "Total Submissions",
        value: stats.totalCount || total,
        subtext: "All time identity verifications",
        icon: ShieldCheck,
        color: "text-blue-500 bg-blue-500/10 border-blue-500/20",
        active: statusFilter === "all",
        onClick: () => {
          setStatusFilter("all");
          setPage(1);
        },
      },
    ],
    [stats, total, statusFilter]
  );

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-500" /> User KYC Verifications
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Review National ID, Live Selfie, PAN Card, and Bank account details to authorize withdrawal requests.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchKycList(page)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium transition-all shadow-sm"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="text-red-500 hover:text-red-700 font-bold">
            ×
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 rounded-xl bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300 text-sm flex items-center justify-between">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage("")} className="text-green-500 hover:text-green-700 font-bold">
            ×
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            onClick={card.onClick}
            className={`bg-white dark:bg-slate-800 rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all cursor-pointer ${
              card.active
                ? "border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-400"
                : "border-gray-200 dark:border-slate-700"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {card.label}
                </p>
                <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{card.value}</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{card.subtext}</p>
              </div>
              <div className={`p-3 rounded-2xl border ${card.color}`}>
                <card.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters & Search */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 space-y-4 shadow-sm">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex items-center p-1 bg-gray-100 dark:bg-slate-700/60 rounded-xl overflow-x-auto custom-scrollbar">
            {["all", "pending", "approved", "rejected"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setPage(1);
                  setStatusFilter(tab);
                }}
                className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all whitespace-nowrap ${
                  statusFilter === tab
                    ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {tab === "all" ? "All Requests" : tab}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, phone, PAN, ID number..."
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* KYC Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-700/30 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={requests.length > 0 && selectedIds.length === requests.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">User</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">National ID</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">PAN Details</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Bank Account</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Submitted At</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Status</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin inline-block mr-2 text-blue-500" />
                    Loading KYC requests...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    No KYC submissions found matching your filters.
                  </td>
                </tr>
              ) : (
                requests.map((item) => {
                  const isPending = item.status === "pending";
                  const isApproved = item.status === "approved";
                  const isRejected = item.status === "rejected";
                  const isSelected = selectedIds.includes(item._id);

                  return (
                    <tr key={item._id} className={`transition-colors ${isSelected ? "bg-blue-50/80 dark:bg-blue-500/10" : "hover:bg-gray-50/60 dark:hover:bg-slate-700/30"}`}>
                      {/* Checkbox */}
                      <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(item._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>

                      {/* User Column */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.user?.mainImage || "/logo512.png"}
                            alt={item.user?.name || "User"}
                            className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700"
                            onError={(e) => {
                              e.target.src = "https://ui-avatars.com/api/?name=" + encodeURIComponent(item.user?.name || "U");
                            }}
                          />
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {item.user?.name || "Unknown User"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              @{item.user?.username || "username"} {item.user?.phone ? `• ${item.user.phone}` : ""}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* National ID Column */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                            {item.idType || "Aadhaar Card"}
                          </span>
                          <p className="font-mono text-xs font-bold text-gray-900 dark:text-white">
                            {item.idNumber}
                          </p>
                        </div>
                      </td>

                      {/* PAN Details Column */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="space-y-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">PAN Number</span>
                          <p className="font-mono text-xs font-bold text-gray-900 dark:text-white">
                            {item.panNumber}
                          </p>
                        </div>
                      </td>

                      {/* Bank Details Column */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="space-y-0.5 text-xs">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {item.bankDetails?.accountHolderName || "Holder N/A"}
                          </p>
                          <p className="font-mono text-gray-600 dark:text-gray-400">
                            A/C: {item.bankDetails?.accountNumber}
                          </p>
                          <p className="font-mono text-gray-500 text-[11px]">
                            IFSC: {item.bankDetails?.ifscCode} {item.bankDetails?.bankName ? `(${item.bankDetails.bankName})` : ""}
                          </p>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-xs text-gray-500 dark:text-gray-400">
                        {item.submittedAt ? new Date(item.submittedAt).toLocaleString("en-IN") : "-"}
                      </td>

                      {/* Status */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                            isPending
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                              : isApproved
                              ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                          }`}
                        >
                          {isPending && <Clock className="w-3.5 h-3.5" />}
                          {isApproved && <CheckCircle2 className="w-3.5 h-3.5" />}
                          {isRejected && <XCircle className="w-3.5 h-3.5" />}
                          {item.status.toUpperCase()}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right">
                        <button
                          onClick={() => openReviewModal(item)}
                          className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all inline-flex items-center gap-1.5 shadow-sm ${
                            isPending
                              ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90"
                              : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600"
                          }`}
                        >
                          {isPending ? "Review & Verify" : "View Details"}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <button
            onClick={() => fetchKycList(Math.max(1, page - 1))}
            disabled={page <= 1 || loading}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-all"
          >
            Previous
          </button>
          <span>
            Page <strong className="text-gray-900 dark:text-white">{page}</strong> of {totalPages} ({total} items)
          </span>
          <button
            onClick={() => fetchKycList(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || loading}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-all"
          >
            Next
          </button>
        </div>
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={total}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="KYC records"
      />

      {/* REVIEW & INSPECTION MODAL */}
      {isModalOpen && selectedKyc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-500">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Review KYC Submission
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Verify uploaded identification documents, selfie photo & bank details
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1 custom-scrollbar">
              {/* User Info Bar */}
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-slate-700/40 border border-gray-200/80 dark:border-slate-600/60 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedKyc.user?.mainImage || "/logo512.png"}
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">
                      {selectedKyc.user?.name || "User"}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      @{selectedKyc.user?.username} {selectedKyc.user?.phone ? `• ${selectedKyc.user.phone}` : ""}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      Wallet Balance: {formatNumber(selectedKyc.user?.wallet?.balanceCoins || 0)} Coins
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedKyc.status === "pending"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                        : selectedKyc.status === "approved"
                        ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                        : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`}
                  >
                    {selectedKyc.status.toUpperCase()}
                  </span>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Submitted: {selectedKyc.submittedAt ? new Date(selectedKyc.submittedAt).toLocaleString("en-IN") : "-"}
                  </p>
                </div>
              </div>

              {/* Uploaded Documents Grid */}
              <div>
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  Uploaded Document Photos
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {/* National ID Front */}
                  <div className="bg-gray-50 dark:bg-slate-700/40 rounded-2xl border border-gray-200 dark:border-slate-700 p-3 space-y-2 flex flex-col">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      1. ID Front ({selectedKyc.idType || "National ID"})
                    </span>
                    <div className="relative group rounded-xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center border border-gray-200 dark:border-slate-600 flex-1">
                      {selectedKyc.idFrontImage ? (
                        <>
                          <img
                            src={selectedKyc.idFrontImage}
                            alt="ID Front"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            onClick={() => setPreviewImage(selectedKyc.idFrontImage)}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity gap-1 text-xs font-semibold"
                          >
                            <Maximize2 className="w-4 h-4" /> View Full
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>
                  </div>

                  {/* National ID Back (Optional) */}
                  <div className="bg-gray-50 dark:bg-slate-700/40 rounded-2xl border border-gray-200 dark:border-slate-700 p-3 space-y-2 flex flex-col">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      2. ID Back (Optional)
                    </span>
                    <div className="relative group rounded-xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center border border-gray-200 dark:border-slate-600 flex-1">
                      {selectedKyc.idBackImage ? (
                        <>
                          <img
                            src={selectedKyc.idBackImage}
                            alt="ID Back"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            onClick={() => setPreviewImage(selectedKyc.idBackImage)}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity gap-1 text-xs font-semibold"
                          >
                            <Maximize2 className="w-4 h-4" /> View Full
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">Not Uploaded</span>
                      )}
                    </div>
                  </div>

                  {/* Live Face Selfie */}
                  <div className="bg-gray-50 dark:bg-slate-700/40 rounded-2xl border border-gray-200 dark:border-slate-700 p-3 space-y-2 flex flex-col">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      3. Live Face Selfie
                    </span>
                    <div className="relative group rounded-xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center border border-gray-200 dark:border-slate-600 flex-1">
                      {selectedKyc.faceSelfieImage ? (
                        <>
                          <img
                            src={selectedKyc.faceSelfieImage}
                            alt="Face Selfie"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            onClick={() => setPreviewImage(selectedKyc.faceSelfieImage)}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity gap-1 text-xs font-semibold"
                          >
                            <Maximize2 className="w-4 h-4" /> View Full
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>
                  </div>

                  {/* PAN Card */}
                  <div className="bg-gray-50 dark:bg-slate-700/40 rounded-2xl border border-gray-200 dark:border-slate-700 p-3 space-y-2 flex flex-col">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      4. PAN Card Photo
                    </span>
                    <div className="relative group rounded-xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center border border-gray-200 dark:border-slate-600 flex-1">
                      {selectedKyc.panImage ? (
                        <>
                          <img
                            src={selectedKyc.panImage}
                            alt="PAN Card"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            onClick={() => setPreviewImage(selectedKyc.panImage)}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity gap-1 text-xs font-semibold"
                          >
                            <Maximize2 className="w-4 h-4" /> View Full
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ID & PAN Details */}
                <div className="p-4 rounded-2xl bg-purple-50/50 dark:bg-purple-500/5 border border-purple-200 dark:border-purple-500/20 space-y-3">
                  <h4 className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                    <FileText className="w-4 h-4" /> Identity & Tax Details
                  </h4>
                  <div className="space-y-2 bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs">
                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">ID Type:</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {selectedKyc.idType || "Aadhaar Card"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">ID Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedKyc.idNumber}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedKyc.idNumber, "modal_id")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_id" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">PAN Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedKyc.panNumber}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedKyc.panNumber, "modal_pan")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_pan" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bank Account Details */}
                <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-500/5 border border-blue-200 dark:border-blue-500/20 space-y-3">
                  <h4 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" /> Bank Account Details
                  </h4>
                  <div className="space-y-2 bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs">
                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">Account Holder:</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {selectedKyc.bankDetails?.accountHolderName || "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedKyc.bankDetails?.accountNumber || "N/A"}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedKyc.bankDetails?.accountNumber, "modal_bank_acc")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_bank_acc" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">IFSC Code:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedKyc.bankDetails?.ifscCode || "N/A"}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedKyc.bankDetails?.ifscCode, "modal_bank_ifsc")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_bank_ifsc" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Bank Name:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {selectedKyc.bankDetails?.bankName || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Review Input / Status Feedback */}
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Rejection Reason (Required only if Rejecting)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., PAN card image is blurry or Name does not match Bank account"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    disabled={processingStatus}
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Admin Internal Note (Optional)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Internal verification notes for admins..."
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    disabled={processingStatus}
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Close
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleStatusUpdate("rejected")}
                  disabled={processingStatus}
                  className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  {processingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Reject KYC
                </button>

                <button
                  type="button"
                  onClick={() => handleStatusUpdate("approved")}
                  disabled={processingStatus}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50 flex items-center gap-1.5 transition-all shadow-md"
                >
                  {processingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Approve KYC
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FULLSCREEN IMAGE PREVIEW LIGHTBOX */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={previewImage}
              alt="KYC Document Preview"
              className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl border border-white/20"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 flex items-center gap-4">
              <a
                href={previewImage}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-gray-900 text-xs font-semibold hover:bg-gray-100 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-4 h-4" /> Open Original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
